import React from 'react';

/**
 * Responsive picture element that serves WebP with a JPG fallback and a
 * 640-wide mobile WebP variant. Every callsite MUST pass explicit width
 * and height to prevent layout shift.
 *
 * The passed `className` is applied to BOTH the <picture> wrapper AND the
 * child <img>. This is deliberate — without a class on the wrapper the
 * picture element defaults to display:inline and shrink-wraps to the
 * img's intrinsic size, breaking any parent that expected the img itself
 * to fill it. Applying the class to both means layout utilities
 * (w-full, h-full, max-w-*, mx-*, about-image, etc.) size the wrapper as
 * the layout box while paint-only utilities (object-contain, rounded-*,
 * group-hover:scale-*) still apply to the img where they belong.
 *
 * Props:
 *   base       – path stem without extension, e.g. "/api/img/product-1"
 *   alt        – existing alt text (preserved verbatim by the caller)
 *   width      – natural width in px (kept as attribute for aspect-ratio hint)
 *   height     – natural height in px (kept as attribute for aspect-ratio hint)
 *   className  – applied to BOTH picture wrapper and img (see above)
 *   lcp        – if true: eager + fetchPriority=high, else lazy
 *   sizes      – optional sizes attribute
 *   dataTestId – optional data-testid passed to the <img>
 */
const PictureImg = ({ base, alt, width, height, className, lcp = false, sizes, dataTestId, ...rest }) => {
  const webp = `${base}.webp`;
  const webp640 = `${base}-640.webp`;
  const jpg = `${base}.jpg`;

  return (
    <picture className={className} style={{ display: 'block' }}>
      <source media="(max-width: 640px)" srcSet={webp640} type="image/webp" />
      <source srcSet={webp} type="image/webp" sizes={sizes} />
      <img
        src={jpg}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={lcp ? 'eager' : 'lazy'}
        decoding="async"
        {...(lcp ? { fetchPriority: 'high' } : {})}
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
        {...rest}
      />
    </picture>
  );
};

export default PictureImg;
